// enci.popup.js

import { debugLog } from './enci.log.js';

/**
 * Configures popups for both image galleries and video/maps iframes.
 *
 * @throws {Error} If there's an issue with initializing the popups.
 */
export function setMagnificPopup() {
  debugLog('setMagnificPopup');

  try {
    const popupGalleries = document.querySelectorAll('.popup-gallery');
    const videoPopups = document.querySelectorAll('.popup-youtube, .popup-vimeo, .popup-gmaps');

    if (!popupGalleries.length && !videoPopups.length) {
      debugLog('setMagnificPopup >> No popup elements found.', { type: 'error' });
      return;
    }

    // Setup popups for image galleries
    if (popupGalleries.length) {
      setupImageGalleries(popupGalleries);
    }

    // Setup popups for video popups
    if (videoPopups.length) {
      setupVideoPopups(videoPopups);
    }
  } catch (error) {
    debugLog(`setMagnificPopup >> Error: ${error.message}`, { type: 'error' });
  }
}

/**
 * Sets up image gallery popups.
 *
 * @param {NodeList} galleries - The gallery elements to set up.
 */
function setupImageGalleries(galleries) {
  galleries.forEach(gallery => {
    const links = gallery.querySelectorAll('a.popup-img');
    links.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        openImagePopup(link, links);
      });
    });
  });
}

/**
 * Opens an image popup.
 *
 * @param {HTMLElement} clickedLink - The clicked image link.
 * @param {NodeList} allLinks - All image links in the gallery.
 */
function openImagePopup(clickedLink, allLinks) {
  const overlay = createOverlay();
  const popupContent = createPopupContent(clickedLink);
  overlay.appendChild(popupContent);

  const closeButton = createCloseButton();
  popupContent.appendChild(closeButton);

  if (allLinks.length > 1) {
    const [prevButton, nextButton] = createNavigationButtons();
    popupContent.appendChild(prevButton);
    popupContent.appendChild(nextButton);

    let currentIndex = Array.from(allLinks).indexOf(clickedLink);
    prevButton.addEventListener('click', () => navigateGallery(allLinks, currentIndex, -1));
    nextButton.addEventListener('click', () => navigateGallery(allLinks, currentIndex, 1));
  }

  document.body.appendChild(overlay);
}

/**
 * Sets up video popups.
 *
 * @param {NodeList} videoLinks - The video link elements to set up.
 */
function setupVideoPopups(videoLinks) {
  videoLinks.forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      openVideoPopup(link.href);
    });
  });
}

/**
 * Opens a video popup.
 *
 * @param {string} videoUrl - The URL of the video to display.
 */
function openVideoPopup(videoUrl) {
  const overlay = createOverlay();
  const iframe = document.createElement('iframe');
  iframe.src = videoUrl;
  iframe.style.width = '80%';
  iframe.style.height = '80%';
  iframe.style.border = 'none';

  overlay.appendChild(iframe);

  const closeButton = createCloseButton();
  overlay.appendChild(closeButton);

  document.body.appendChild(overlay);
}

/**
 * Creates an overlay element for the popup.
 *
 * @returns {HTMLElement} The created overlay element.
 */
function createOverlay() {
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
  overlay.style.display = 'flex';
  overlay.style.justifyContent = 'center';
  overlay.style.alignItems = 'center';
  overlay.style.zIndex = '9999';

  overlay.addEventListener('click', e => {
    if (e.target === overlay) {
      document.body.removeChild(overlay);
    }
  });

  return overlay;
}

/**
 * Creates the content for an image popup.
 *
 * @param {HTMLElement} link - The link element containing image data.
 * @returns {HTMLElement} The created popup content.
 */
function createPopupContent(link) {
  const content = document.createElement('div');
  content.style.position = 'relative';
  content.style.maxWidth = '90%';
  content.style.maxHeight = '90%';

  const img = document.createElement('img');
  img.src = link.href;
  img.style.maxWidth = '100%';
  img.style.maxHeight = '100%';
  img.style.objectFit = 'contain';

  const caption = document.createElement('div');
  caption.style.position = 'absolute';
  caption.style.bottom = '0';
  caption.style.left = '0';
  caption.style.width = '100%';
  caption.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  caption.style.color = 'white';
  caption.style.padding = '10px';

  const title = link.dataset.title;
  const seller = link.dataset.seller;
  caption.textContent = `${title} by ${seller}`;

  content.appendChild(img);
  content.appendChild(caption);

  return content;
}

/**
 * Creates a close button for the popup.
 *
 * @returns {HTMLElement} The created close button.
 */
function createCloseButton() {
  const closeButton = document.createElement('button');
  closeButton.textContent = 'Close';
  closeButton.style.position = 'absolute';
  closeButton.style.top = '10px';
  closeButton.style.right = '10px';
  closeButton.addEventListener('click', () => {
    const overlay = closeButton.closest('div');
    document.body.removeChild(overlay);
  });
  return closeButton;
}

/**
 * Creates navigation buttons for the image gallery.
 *
 * @returns {HTMLElement[]} An array containing the previous and next buttons.
 */
function createNavigationButtons() {
  const prevButton = document.createElement('button');
  prevButton.textContent = 'Previous';
  prevButton.style.position = 'absolute';
  prevButton.style.left = '10px';
  prevButton.style.top = '50%';

  const nextButton = document.createElement('button');
  nextButton.textContent = 'Next';
  nextButton.style.position = 'absolute';
  nextButton.style.right = '10px';
  nextButton.style.top = '50%';

  return [prevButton, nextButton];
}

/**
 * Navigates the gallery to the next or previous image.
 *
 * @param {NodeList} allLinks - All image links in the gallery.
 * @param {number} currentIndex - The index of the current image.
 * @param {number} direction - The direction to navigate (-1 for previous, 1 for next).
 */
function navigateGallery(allLinks, currentIndex, direction) {
  const newIndex = (currentIndex + direction + allLinks.length) % allLinks.length;
  const newLink = allLinks[newIndex];
  const overlay = document.querySelector('div[style*="position: fixed"]');
  document.body.removeChild(overlay);
  openImagePopup(newLink, allLinks);
}
